import { useCallback, useReducer, type SetStateAction } from "react";

// useState with undo and redo (#336, brief §9: «annulla e ripeti per azione,
// profondità 50»). Same setter contract as useState (a value or an updater),
// so it drops in where a draft was a plain useState.
//
// One entry per ACTION, not per call: a call joins the previous entry only
// when it lands within `coalesceMs` of it AND changes the same single field
// (editKey below). That is what turns a word typed into a name field into one
// undo instead of eight, and a burst of stepper taps on one cell into one
// step, while a structural action (a day removed, a slot moved, anything that
// touches more than one field) is always a step of its own. The window slides
// with each call; undo, redo, reset and seal close it, so the first edit after
// an undo or a save always opens a new entry.
//
// A setter call that returns the SAME value (reference) records nothing: an
// updater that decides there is nothing to change must not cost an undo.
//
// `rebase` rewrites every entry (past, present, future) without recording one.
// It is for facts that are not edits: the publish status a save just wrote
// must not come back on undo, or the next save would silently revert it.

export type HistoryControls<T> = {
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /** Replace the value and forget the history (a resumed or reloaded draft). */
  reset: (value: T) => void;
  /** Apply `fn` to every entry without recording a step. */
  rebase: (fn: (value: T) => T) => void;
  /** Close the coalesce window: the next edit opens a new entry (a save). */
  seal: () => void;
};

type Hist<T> = {
  past: T[];
  present: T;
  future: T[];
  lastAt: number;
  /** The one field the present entry's last edit changed, or null. */
  lastKey: string | null;
};

type Action<T> =
  | { type: "set"; next: SetStateAction<T>; at: number }
  | { type: "undo" }
  | { type: "redo" }
  | { type: "reset"; value: T }
  | { type: "rebase"; fn: (value: T) => T }
  | { type: "seal" };

const resolve = <T,>(next: SetStateAction<T>, prev: T): T =>
  typeof next === "function" ? (next as (p: T) => T)(prev) : next;

const STRUCTURAL = Symbol("structural");
type Leaves = string[] | typeof STRUCTURAL;

const isObj = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object";

// The leaf paths that differ between two immutable values, walking only the
// branches whose reference changed. STRUCTURAL as soon as a shape changes (an
// array's length, an object appearing or vanishing) or more than one leaf does.
function changedLeaves(a: unknown, b: unknown, path: string, out: string[]): Leaves {
  if (a === b) return out;
  if (!isObj(a) || !isObj(b)) {
    if (isObj(a) || isObj(b)) return STRUCTURAL;
    out.push(path);
    return out.length > 1 ? STRUCTURAL : out;
  }
  if (Array.isArray(a) !== Array.isArray(b)) return STRUCTURAL;
  if (Array.isArray(a) && Array.isArray(b) && a.length !== b.length) return STRUCTURAL;
  for (const k of new Set([...Object.keys(a), ...Object.keys(b)])) {
    if (changedLeaves(a[k], b[k], `${path}/${k}`, out) === STRUCTURAL) return STRUCTURAL;
  }
  return out;
}

/** The single field an edit changed (its path), or null: several fields, or
 *  a change of shape. Only edits with the same non-null key coalesce. */
export function editKey(prev: unknown, next: unknown): string | null {
  const leaves = changedLeaves(prev, next, "", []);
  return leaves === STRUCTURAL || leaves.length !== 1 ? null : leaves[0]!;
}

export function historyReducer<T>(
  depth: number,
  coalesceMs: number,
): (state: Hist<T>, action: Action<T>) => Hist<T> {
  return (state, action) => {
    switch (action.type) {
      case "set": {
        const value = resolve(action.next, state.present);
        if (value === state.present) return state;
        const key = editKey(state.present, value);
        const joins =
          key !== null &&
          key === state.lastKey &&
          state.past.length > 0 &&
          state.lastAt > 0 &&
          action.at - state.lastAt < coalesceMs;
        const past = joins ? state.past : [...state.past, state.present].slice(-depth);
        return { past, present: value, future: [], lastAt: action.at, lastKey: key };
      }
      case "undo": {
        const prev = state.past[state.past.length - 1];
        if (state.past.length === 0) return state;
        return {
          past: state.past.slice(0, -1),
          present: prev as T,
          future: [state.present, ...state.future],
          lastAt: 0,
          lastKey: null,
        };
      }
      case "redo": {
        if (state.future.length === 0) return state;
        const [next, ...rest] = state.future;
        return {
          past: [...state.past, state.present].slice(-depth),
          present: next as T,
          future: rest,
          lastAt: 0,
          lastKey: null,
        };
      }
      case "reset":
        return { past: [], present: action.value, future: [], lastAt: 0, lastKey: null };
      case "seal":
        return state.lastAt === 0 ? state : { ...state, lastAt: 0, lastKey: null };
      case "rebase":
        return {
          past: state.past.map(action.fn),
          present: action.fn(state.present),
          future: state.future.map(action.fn),
          lastAt: state.lastAt,
          lastKey: state.lastKey,
        };
    }
  };
}

export function useHistoryState<T>(
  initial: T | (() => T),
  { depth = 50, coalesceMs = 800 }: { depth?: number; coalesceMs?: number } = {},
): [T, (next: SetStateAction<T>) => void, HistoryControls<T>] {
  const [state, dispatch] = useReducer(
    historyReducer<T>(depth, coalesceMs),
    initial,
    (init): Hist<T> => ({
      past: [],
      present: typeof init === "function" ? (init as () => T)() : init,
      future: [],
      lastAt: 0,
      lastKey: null,
    }),
  );
  const set = useCallback(
    (next: SetStateAction<T>) => dispatch({ type: "set", next, at: Date.now() }),
    [],
  );
  const undo = useCallback(() => dispatch({ type: "undo" }), []);
  const redo = useCallback(() => dispatch({ type: "redo" }), []);
  const reset = useCallback((value: T) => dispatch({ type: "reset", value }), []);
  const rebase = useCallback(
    (fn: (value: T) => T) => dispatch({ type: "rebase", fn }),
    [],
  );
  const seal = useCallback(() => dispatch({ type: "seal" }), []);
  return [
    state.present,
    set,
    {
      undo,
      redo,
      reset,
      rebase,
      seal,
      canUndo: state.past.length > 0,
      canRedo: state.future.length > 0,
    },
  ];
}
